import { portalAdmin } from './portalAdmin';

export interface User {
  username: string;
  email: string;
  enabled: boolean;
  status: string;
  created: Date;
  /** In the staff group. A person outside it can sign in, and every page then refuses them. */
  staff: boolean;
}

export interface ListUsersResult {
  users: User[];
  nextToken?: string;
  hasMore: boolean;
}

interface WireUser extends Omit<User, 'created'> {
  created: string | null;
}

/**
 * The portal's own users. The browser holds no Cognito rights on the portal
 * pool (TASK-635): each call is a portal-admin operation, which checks the
 * caller's staff group. A new user gets a random password nobody sees, and
 * goes into the staff group.
 */
export class UserManagementService {
  // The constructor keeps its old shape, so the page builds it as before.
  constructor(_region?: string, _identityPoolId?: string, _userPoolId?: string) {}

  async createUser(email: string): Promise<{ username: string; email: string }> {
    return portalAdmin('portalUsers.create', { email: email.toLowerCase() });
  }

  async listUsers(limit = 20, paginationToken?: string): Promise<ListUsersResult> {
    const page = await portalAdmin<{ users: WireUser[]; nextToken?: string; hasMore: boolean }>('portalUsers.list', {
      limit: Math.min(Math.max(limit, 1), 60),
      ...(paginationToken && { paginationToken }),
    });
    return {
      ...page,
      users: page.users.map((u) => ({ ...u, created: u.created ? new Date(u.created) : new Date(0) })),
    };
  }

  /** Signs the person out everywhere, then deletes them. A person already gone is not an error. */
  async deleteUser(username: string): Promise<void> {
    await portalAdmin('portalUsers.delete', { username });
  }

  async describeUserPool(): Promise<{ estimatedNumberOfUsers: number }> {
    return portalAdmin('portalUsers.count');
  }
}
